export interface FlowRouteParams {
  model?: string | number
  prompt?: string
  style?: string | number
  type?: string | number
  aspectRatio?: string | number
  effect?: string
}

export interface FlowIterationParams {
  prompt?: string | null
  type?: string | number | null
  style?: string | number | null
  styleId?: string | null
  complexity?: number | null
  strokeWidth?: number | null
  strokeStyle?: number | null
  models?: readonly (string | number)[] | null
  aspectRatio?: string | number | null
  imageId?: string | null
  imageUrl?: string | null
  /** CDN URLs for mentionable reference images, thumbnail order (`@img1` …). */
  referenceUrls?: readonly string[] | null
  sourceVideoUrl?: string | null
}

interface NewFlowByIterationOptions {
  /**
   * When true, appends `force_submit=true` so the flow auto-generates on arrival.
   */
  forceSubmit?: boolean
}

const FLOW_PARAM_KEYS = [
  'prompt',
  'type',
  'style',
  'styleId',
  'complexity',
  'strokeWidth',
  'strokeStyle',
  'aspectRatio',
  'imageId',
  'imageUrl',
] as const satisfies readonly (keyof Omit<
  FlowIterationParams,
  'models' | 'referenceUrls' | 'sourceVideoUrl'
>)[]

export const FLOW_REFERENCE_URL_SEARCH_PARAM = 'referenceUrl'

/**
 * Every URL the Flow tool owns, rebased on where the router mounted it: Studio serves Flow at
 * `/flow` and the Unlimited image generator at `/unlimited/image-generator`. Links *into* Flow
 * from elsewhere keep using the `ROUTES.FLOW` table below.
 */
export function flowRoutes(root: string) {
  function rootWithParams(params: FlowRouteParams) {
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value))
      }
    }
    const queryString = searchParams.toString()
    return queryString ? `${root}?${queryString}` : root
  }

  function newFlowByIteration(
    iteration?: FlowIterationParams,
    options?: NewFlowByIterationOptions,
  ) {
    if (!iteration) {
      return root
    }
    const queryParams = new URLSearchParams()
    for (const key of FLOW_PARAM_KEYS) {
      const value = iteration[key]
      if (value !== null && value !== undefined && value !== '' && value !== 0) {
        queryParams.append(key, String(value))
      }
    }
    for (const model of iteration.models ?? []) {
      if (model !== '' && model !== 0) {
        queryParams.append('model', String(model))
      }
    }
    for (const url of iteration.referenceUrls ?? []) {
      if (url) {
        queryParams.append(FLOW_REFERENCE_URL_SEARCH_PARAM, url)
      }
    }
    if (iteration.sourceVideoUrl) {
      queryParams.append('sourceVideoUrl', iteration.sourceVideoUrl)
    }
    if (options?.forceSubmit) {
      queryParams.append('force_submit', 'true')
    }
    return `${root}?${queryParams.toString()}`
  }

  return {
    ROOT: root,
    ROOT_PARAMS: rootWithParams,
    FLOW: (flowId?: string) => (flowId ? `${root}/${flowId}` : `${root}/:flowId`),
    NEW_FLOW_BY_ITERATION: newFlowByIteration,
    COMMUNITY: root,
    TEMPLATES: `${root}/templates`,
    MY_FLOWS: `${root}/my-flows`,
    LIGHTBOX: (flowId: string, imageId: string) => `${root}/lightbox/${flowId}/${imageId}`,
    FAVORITES: `${root}/favorites`,
    USER_NEWEST: (slug: string) => `${root}/user/${slug}/newest`,
    USER_TOP: (slug: string) => `${root}/user/${slug}/top`,
  }
}

export type FlowRoutes = ReturnType<typeof flowRoutes>

export const FLOW_ROOT = '/flow'

export const ROUTES = flowRoutes(FLOW_ROOT)
